import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import usePageTitle from "../hooks/usePageTitle";
import { FaLaptop, FaPlus, FaTimes, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

// Helper to compare dates
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Helper to get calendar days for a month
function getCalendarDays(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Get the first day of the month
  const firstDay = new Date(year, month, 1);
  // Get the last day of the month
  const lastDay = new Date(year, month + 1, 0);
  
  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDay.getDay();
  
  // Get the last day of the previous month
  const prevMonthLastDay = new Date(year, month, 0);
  const daysInPrevMonth = prevMonthLastDay.getDate();
  
  // Get the number of days in the current month
  const daysInMonth = lastDay.getDate();
  
  // Calculate the number of days from the previous month to show
  const daysFromPrevMonth = firstDayOfWeek;
  
  // Calculate the number of days from the next month to show
  const daysFromNextMonth = 42 - (daysFromPrevMonth + daysInMonth); // 6 rows x 7 days = 42
  
  const days = [];
  
  // Add days from previous month
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    const day = new Date(year, month - 1, daysInPrevMonth - i);
    days.push({
      date: day,
      isCurrentMonth: false,
      isToday: false,
      isWeekend: [0, 6].includes(day.getDay()),
      dateNum: day.getDate()
    });
  }
  
  // Add days from current month
  for (let i = 1; i <= daysInMonth; i++) {
    const day = new Date(year, month, i);
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    
    days.push({
      date: day,
      isCurrentMonth: true,
      isToday: dayStart.getTime() === today.getTime(),
      isWeekend: [0, 6].includes(day.getDay()),
      dateNum: i
    });
  }
  
  // Add days from next month
  for (let i = 1; i <= daysFromNextMonth; i++) {
    const day = new Date(year, month + 1, i);
    days.push({
      date: day,
      isCurrentMonth: false,
      isToday: false,
      isWeekend: [0, 6].includes(day.getDay()),
      dateNum: i
    });
  }
  
  return days;
}

// Map of Tailwind color classes to their hex values with a more vibrant palette
const colorMap = {
  'bg-indigo-500': '#6366F1',
  'bg-blue-500': '#3B82F6',
  'bg-cyan-500': '#06B6D4',
  'bg-teal-500': '#14B8A6',
  'bg-emerald-500': '#10B981',
  'bg-green-500': '#22C55E',
  'bg-lime-500': '#84CC16',
  'bg-yellow-500': '#EAB308',
  'bg-amber-500': '#F59E0B',
  'bg-orange-500': '#F97316',
  'bg-red-500': '#EF4444',
  'bg-pink-500': '#EC4899',
  'bg-rose-500': '#F43F5E',
  'bg-purple-500': '#A855F7',
  'bg-violet-500': '#8B5CF6',
  'bg-fuchsia-500': '#D946EF'
};

// Array of Tailwind color classes for events
const colorOptions = Object.keys(colorMap);

// Function to get a random color from the options
const getRandomColor = () => {
  return colorOptions[Math.floor(Math.random() * colorOptions.length)];
};

// Function to get a contrasting text color (black or white) based on background color
const getContrastColor = (bgColor) => {
  // These colors look better with white text
  const darkColors = ['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500', 'bg-rose-500'];
  return darkColors.includes(bgColor) ? 'bg-white' : 'bg-gray-900';
};

// Function to fetch birthdays from the backend API
const fetchBirthdays = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('https://dev.sliitleo.org/Backend/membersBirthdays.php', {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });
    const text = await response.text();
    try {
      return JSON.parse(text).data;
    } catch (jsonErr) {
      console.error('Raw response from membersBirthdays.php:', text);
      throw jsonErr;
    }
  } catch (error) {
    console.error('Error fetching birthdays:', error);
    return [];
  }
};

// Function to fetch events from the backend API
const fetchEvents = async (startDate, endDate) => {
  try {
    const token = localStorage.getItem('token');
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');
    
    // Build the URL with query parameters
    const url = new URL('https://dev.sliitleo.org/Backend/get_events.php');
    url.searchParams.append('start', start);
    url.searchParams.append('end', end);
    
    // Prepare headers
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: headers,
      mode: 'cors',
      cache: 'no-cache'
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // If we can't parse JSON, use the status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    
    // Map the API response to match the expected event format
    return data.map(event => {
      const startDate = new Date(event.start);
      const endDate = new Date(event.end || event.start);
      const isAllDay = event.allDay || (!event.start.includes('T') && !event.end?.includes('T'));
      
      // Get a random color from colorMap for each event
      const colorKeys = Object.keys(colorMap);
      const randomColor = colorKeys[Math.floor(Math.random() * colorKeys.length)];
      
      return {
        ...event,
        id: event.id.toString(),
        title: event.title || 'No Title',
        date: startDate,
        endDate: endDate,
        start: event.start,
        end: event.end || event.start,
        allDay: isAllDay,
        time: isAllDay ? 'All Day' : 
          (startDate.getHours() ? format(startDate, 'h:mm a') : '') + 
          (endDate.getHours() ? ` - ${format(endDate, 'h:mm a')}` : ''),
        description: event.description || '',
        location: event.location || '',
        color: randomColor || 'bg-indigo-500',
        dotColor: getContrastColor(randomColor || 'bg-indigo-500')
      };
    });
  } catch (error) {
    console.error('Error fetching events:', error);
    return [];
  }
};

// Function to create a new event
const createEvent = async (eventData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('https://dev.sliitleo.org/Backend/calendar.php', {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title: eventData.title,
        description: eventData.description || '',
        start_date: eventData.date,
        end_date: eventData.date,
        start_time: eventData.startTime,
        end_time: eventData.endTime,
        all_day: false,
        location: eventData.location || '',
        color: eventData.color || 'bg-indigo-500'
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create event');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

// Function to update an event
const updateEvent = async (eventId, eventData) => {
  try {
    if (!eventId) throw new Error('No event ID provided for update.');
    const token = localStorage.getItem('token');
    const response = await fetch(`https://dev.sliitleo.org/Backend/calendar.php?id=${eventId}`, {
      method: 'PUT',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error('Server returned non-JSON response: ' + text.slice(0, 200));
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update event');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

// Function to delete an event
const deleteEvent = async (eventId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://dev.sliitleo.org/Backend/calendar.php?id=${eventId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete event');
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

// Convert member data to calendar events (from membersBirthdays.php)
const getBirthdayEvents = (members) => {
  const currentYear = new Date().getFullYear();
  return members
    .filter(member => member.DOB)
    .map(member => {
      try {
        const birthDate = new Date(member.DOB);
        if (isNaN(birthDate.getTime())) return null;
        const birthDay = birthDate.getDate();
        const birthMonth = birthDate.getMonth();
        const thisYearsBirthday = new Date(currentYear, birthMonth, birthDay);
        return {
          id: `birthday-${member.id}`,
          title: `${member.name}'s Birthday${member.role ? ` (${member.role})` : ''}`,
          date: thisYearsBirthday,
          time: 'All Day',
          description: 'Birthday',
          location: '',
          isBirthday: true,
          member: member,
          color: 'bg-pink-500',
          dotColor: getContrastColor('bg-pink-500')
        };
      } catch (error) {
        return null;
      }
    })
    .filter(Boolean);
};

// Sample events data
const sampleEvents = [
  {
    date: new Date(2024, 6, 9),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '09:00 - 12:00',
    description: 'Monday, Feb 15',
    location: 'Main Hall',
    color: 'bg-indigo-500'
  },
  {
    date: new Date(2025, 6, 14),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '10:00 - 15:00',
    description: 'Community service',
    location: 'City Park',
    color: 'bg-green-500'
  },
  {
    date: new Date(2025, 6, 19),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '13:00 - 16:00',
    description: 'Leadership skills',
    location: 'Conference Room',
    color: 'bg-blue-500'
  },
  {
    date: new Date(2025, 6, 29),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '18:00 - 22:00',
    description: 'End of month party',
    location: 'Club House',
    color: 'bg-purple-500'
  }
].map(event => ({
  ...event,
  dotColor: getContrastColor(event.color)
}));

// Get background color and style for a date based on events
const getDateStyle = (date, events) => {
  const dayEvents = events.filter(event => isSameDay(event.date, date));
  
  if (dayEvents.length > 0) {
    // For multiple events, create a gradient or pattern
    if (dayEvents.length > 1) {
      const colors = dayEvents.map(event => colorMap[event.color] || '#6366F1');
      return {
        background: `linear-gradient(135deg, ${colors.join(', ')})`,
        backgroundSize: '200% 200%',
        animation: 'gradient 4s ease infinite',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(-2px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
        }
      };
    }
    // For single event, use solid color with shadow
    return { 
      backgroundColor: colorMap[dayEvents[0].color] || '#6366F1',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
      }
    };
  }
  return {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      transform: 'translateY(-2px)'
    }
  };
};

// Add global styles for the calendar
const style = document.createElement('style');
style.textContent = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  /* Smooth scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  /* Calendar day hover effect */
  .calendar-day {
    transition: all 0.2s ease;
  }
  
  .calendar-day:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.05);
  }
  
  /* Event card hover effect */
  .event-card {
    transition: all 0.3s ease;
  }
  
  .event-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }
`;
document.head.appendChild(style);

// Event modal component
const EventModal = ({ isOpen, onClose, onSave, selectedDate = new Date(), isSaving = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: format(selectedDate, 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    color: colorOptions[Math.floor(Math.random() * colorOptions.length)]
  });

  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      date: new Date(formData.date + 'T' + formData.startTime)
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        ref={modalRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2 }}
        className="bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden border border-gray-600"
      > 
      </motion.div>
    </div>
  );
};

const EventActionsMenu = ({ event, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-gray-400 hover:text-white p-1 -mr-2"
        aria-label="Event actions"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
      </button>
      
       
    </div>
  );
};

export default function ClubCalander() {
  // Get user info from localStorage
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem('user')) || {
      name: 'Unknown User',
      role: 'Member',
      image: '/user.png',
    }
  );
  const [date, setDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [birthdayEvents, setBirthdayEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // State for error handling
  const [error, setError] = useState(null);

  // Form state for event modal
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    color: colorOptions[Math.floor(Math.random() * colorOptions.length)],
    all_day: false
  });
  
  // Refs
  const eventFormRef = useRef(null);
  
  // Fetch events from the API
  const loadEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the first and last day of the current month
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      // Fetch events for the current month
      const eventsData = await fetchEvents(firstDay, lastDay);
      setEvents(eventsData);
      
    } catch (error) {
      console.error('Error loading events:', error);
      setError('Failed to load events. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [date]);
  
  // Fetch birthdays
  const loadBirthdays = useCallback(async () => {
    try {
      const members = await fetchBirthdays();
      if (members.length === 0) {
        setError(prev => prev || 'No birthdays found or failed to load birthdays');
      }
      const birthdays = getBirthdayEvents(members);
      setBirthdayEvents(birthdays);
    } catch (error) {
      console.error('Error loading birthdays:', error);
      setError('Failed to load birthdays. Please try again.');
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadEvents(),
        loadBirthdays()
      ]);
    };
    
    loadData();
  }, [loadEvents, loadBirthdays]);
  
  // Combine regular events with birthdays
  const allEvents = useMemo(() => {
    return [...events, ...birthdayEvents];
  }, [events, birthdayEvents]);
  
  const handleAddEvent = async (newEvent) => {
    try {
      setIsSaving(true);
      
      // Format the event data for the API
      const eventData = {
        ...newEvent,
        start_date: format(new Date(newEvent.date), 'yyyy-MM-dd'),
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        color: newEvent.color || 'bg-indigo-500'
      };
      
      let result;
      
      if (editingEvent && editingEvent.id) {
        // Update existing event
        const updatedEvent = await updateEvent(editingEvent.id, eventData);
        
        // Update the local state with the updated event
        setEvents(prev => prev.map(e => 
          e.id === editingEvent.id 
            ? {
                ...updatedEvent,
                date: new Date(updatedEvent.start_date),
                time: updatedEvent.all_day 
                  ? 'All Day' 
                  : `${updatedEvent.start_time} - ${updatedEvent.end_time}`.trim(),
                dotColor: getContrastColor(updatedEvent.color || 'bg-indigo-500')
              }
            : e
        ));
        
        result = true;
        setError({ type: 'success', message: 'Event updated successfully' });
      } else if (!editingEvent) {
        // Create new event
        const createdEvent = await createEvent(eventData);
        
        // Update the local state with the new event
        setEvents(prev => [...prev, {
          ...createdEvent,
          date: new Date(createdEvent.start_date),
          time: createdEvent.all_day 
            ? 'All Day' 
            : `${createdEvent.start_time} - ${createdEvent.end_time}`.trim(),
          dotColor: getContrastColor(createdEvent.color || 'bg-indigo-500')
        }]);
        
        result = true;
        setError({ type: 'success', message: 'Event created successfully' });
      } else {
        setError({ type: 'error', message: 'Cannot update event: missing event ID.' });
        return false;
      }
      
      // Clear any previous errors
      setTimeout(() => setError(null), 3000);
      return result;
      
    } catch (error) {
      console.error('Error saving event:', error);
      setError({ 
        type: 'error', 
        message: error.message || 'Failed to save event. Please try again.' 
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  
  // Helper to safely format dates for input fields
  const safeFormatDate = (date) => {
    if (!date) return '';
    const d = (date instanceof Date) ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return format(d, 'yyyy-MM-dd');
  };

  const openEventModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      // Pre-fill the form with event data
      setFormData({
        title: event.title,
        description: event.description || '',
        date: safeFormatDate(event.date),
        startTime: event.start_time || '09:00',
        endTime: event.end_time || '10:00',
        location: event.location || '',
        color: event.color || 'bg-indigo-500'
      });
    } else {
      setEditingEvent(null);
      // Reset form data
      setFormData({
        title: '',
        description: '',
        date: format(date, 'yyyy-MM-dd'),
        startTime: '09:00',
        endTime: '10:00',
        location: '',
        color: colorOptions[Math.floor(Math.random() * colorOptions.length)]
      });
    }
    setShowEventModal(true);
  };
  
  const handleEditEvent = (event) => {
    openEventModal(event);
  };
  
  const handleDeleteEvent = async (event) => {
    if (window.confirm(`Are you sure you want to delete "${event.title}"?`)) {
      try {
        setIsLoading(true);
        await deleteEvent(event.id);
        
        // Update local state
        setEvents(prev => prev.filter(e => e.id !== event.id));
        
        // Show success message
        setError({ type: 'success', message: 'Event deleted successfully' });
        setTimeout(() => setError(null), 3000);
      } catch (error) {
        console.error('Error deleting event:', error);
        setError({ type: 'error', message: error.message || 'Failed to delete event' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Filter events for the selected month/year
  const monthEvents = allEvents.filter(e =>
    e.date.getFullYear() === date.getFullYear() && e.date.getMonth() === date.getMonth()
  );
  
  // Get upcoming birthdays (next 30 days)
  const upcomingBirthdays = useMemo(() => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    
    return birthdayEvents
      .filter(event => {
        const eventDate = new Date(event.date);
        eventDate.setFullYear(today.getFullYear()); // Normalize to current year for comparison
        return eventDate >= today && eventDate <= nextMonth;
      })
      .sort((a, b) => a.date - b.date);
  }, [birthdayEvents]);

  return (
    <div className="flex bg-gradient-to-br from-gray-900 to-gray-800 text-white min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-5 md:py-10">
        {/* Error/Success message */}
        {error && (
          <div className={`mb-4 p-4 ${
            error.type === 'error' 
              ? 'bg-red-500/10 border-red-500/30 text-red-300' 
              : 'bg-green-500/10 border-green-500/30 text-green-300'
          } border rounded-lg flex justify-between items-center`}>
            <span>{error.message}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-4 p-1 hover:bg-white/10 rounded-full"
              title="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Loading overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-xl shadow-xl flex items-center space-x-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              <span className="text-white">Loading birthdays...</span>
            </div>
          </div>
        )}
        {/* Loading state */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-xl shadow-xl flex items-center space-x-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              <span className="text-white">Loading birthdays...</span>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="backdrop-blur-md bg-gray-800/50 border border-gray-600 rounded-2xl p-6 mb-8 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 p-3 rounded-xl shadow-lg flex items-center justify-center">
                <FaLaptop className="text-white text-2xl" />
              </span>
              <div>
                <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
                  Club Calendar
                </h1>
                <p className="text-gray-400 text-sm">Manage your schedule and events</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/10 rounded-full filter blur-3xl"></div>
            <div className="absolute top-1/3 -left-20 w-96 h-96 bg-purple-500/10 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
              {/* Upcoming Events Section */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-600 rounded-2xl p-6 shadow-xl h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {date.toLocaleString('default', { month: 'long' })}
                        <span className="text-gray-400 font-normal ml-2">{date.getFullYear()}</span>
                      </h2>
                      <p className="text-gray-400">Your schedule at a glance</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 rounded-lg hover:bg-gray-700/50 transition-colors">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mt-6">
                    {upcomingBirthdays.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-pink-400 uppercase tracking-wider mb-3">
                          Upcoming Birthdays
                        </h3>
                        <div className="space-y-3">
                          {upcomingBirthdays.slice(0, 3).map((event, index) => {
  const member = event.member || {};
  return (
    <div 
      key={`bday-${index}`} 
      className="flex items-center p-3 bg-pink-500/10 rounded-lg border border-pink-500/20"
    >
      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-pink-500 flex items-center justify-center text-white font-bold">
        {event.date.getDate()}
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-white">{member.name || event.title}</p>
        <p className="text-xs text-pink-200">
          Birthday: {member.DOB ? new Date(member.DOB).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : event.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </p>
        {member.role && (
          <p className="text-xs text-pink-300">Role: {member.role}</p>
        )}
      </div>
    </div>
  );
})}
                        </div>
                      </div>
                    )}
                    
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      Upcoming Events
                    </h3>
                    
                    {allEvents.length > 0 ? (
                      allEvents
                        .filter(event =>
                          event.date.getMonth() === date.getMonth() &&
                          event.date.getFullYear() === date.getFullYear()
                        )
                        .map((event, index) => (
                          <div 
                            key={index} 
                            className="event-card bg-gray-700/30 hover:bg-gray-700/50 border border-gray-600 rounded-xl p-4 transition-all duration-300"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div 
                                  className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center text-white font-bold"
                                  style={{ backgroundColor: colorMap[event.color] || '#6366F1' }}
                                >
                                  {event.date.getDate().toString().padStart(2, '0')}
                                  <div className="absolute w-1.5 h-1.5 rounded-full bg-white/80 -top-1 -right-1"></div>
                                </div>
                                <div>
                                  <h3 className="font-semibold text-white">{event.title}</h3>
                                  {event.isBirthday && event.member && (
                                    <>
                                      <p className="text-sm text-pink-300 mt-1">Name: {event.member.name}</p>
                                      <p className="text-sm text-pink-200">Birthday: {event.member.DOB ? new Date(event.member.DOB).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</p>
                                      {event.member.role && (
                                        <p className="text-sm text-pink-400">{event.member.role}</p>
                                      )}
                                    </>
                                  )}
                                  {!event.isBirthday && (
                                    <p className="text-sm text-gray-400 mt-1">
                                      {event.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} • {event.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <EventActionsMenu 
                                event={event}
                                onEdit={handleEditEvent}
                                onDelete={handleDeleteEvent}
                              />
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-700/50 flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-300">No events scheduled</h3>
                        <p className="text-gray-500 mt-1">Add an event to get started</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Calendar Section */}
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-600 rounded-2xl p-4 sm:p-6 shadow-xl">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-bold text-white">
                        {date.toLocaleString('default', { month: 'long' })}
                        <span className="text-gray-400 font-normal ml-2">{date.getFullYear()}</span>
                      </h2>
                      <div className="flex items-center bg-gray-700/50 rounded-lg p-1">
                        <button 
                          onClick={() => {
                            const newDate = new Date(date);
                            newDate.setMonth(newDate.getMonth() - 1);
                            setDate(newDate);
                          }}
                          className="p-1.5 rounded-lg text-gray-300 hover:bg-gray-600/50 transition-colors"
                          aria-label="Previous month"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => setDate(new Date())}
                          className="text-sm px-3 py-1.5 mx-1 rounded-lg bg-gray-600/50 text-white hover:bg-gray-500/50 transition-colors"
                        >
                          Today
                        </button>
                        <button 
                          onClick={() => {
                            const newDate = new Date(date);
                            newDate.setMonth(newDate.getMonth() + 1);
                            setDate(newDate);
                          }}
                          className="p-1.5 rounded-lg text-gray-300 hover:bg-gray-600/50 transition-colors"
                          aria-label="Next month"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center bg-gray-700/50 rounded-lg p-1 border border-gray-600">
                      <button className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-500">
                        Month
                      </button>
                      <button className="px-4 py-1.5 text-sm font-medium text-gray-300 rounded-lg transition-colors hover:bg-gray-600/50">
                        Week
                      </button>
                      <button className="px-4 py-1.5 text-sm font-medium text-gray-300 rounded-lg transition-colors hover:bg-gray-600/50">
                        Day
                      </button>
                    </div>
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="overflow-x-auto">
                    <div className="min-w-[600px] sm:min-w-0">
                      <div className="grid grid-cols-7 bg-gray-800/80 border-b border-gray-600">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                          <div key={i} className="py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-400">
                            {day}
                          </div>
                        ))}
                      </div>
                    
                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 bg-gray-800/30">
                      {getCalendarDays(date).map((day, index) => {
                        const dayEvents = allEvents.filter(event => isSameDay(event.date, day.date));
                        const isCurrentMonth = day.isCurrentMonth;
                        const isToday = isSameDay(day.date, new Date());
                        const isSelected = isSameDay(day.date, date);
                        const dateStyle = getDateStyle(day.date, allEvents);
                        const hasBirthday = dayEvents.some(event => event.isBirthday);
                        
                        return (
                          <div 
                            key={index}
                            className={`relative min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 border-b border-r border-gray-600 transition-all duration-300 ${
                              isCurrentMonth ? 'text-white' : 'text-gray-600'
                            } ${isSelected ? 'ring-2 ring-indigo-500 ring-offset-1 ring-offset-gray-800 z-10' : ''} text-xs sm:text-sm`}
                            style={{
                              ...(isCurrentMonth ? dateStyle : { backgroundColor: 'transparent' }),
                              background: isCurrentMonth && dateStyle?.background ? dateStyle.background : 'transparent',
                              cursor: 'pointer',
                            }}
                            onClick={() => setDate(day.date)}
                          >
                            <div className="flex flex-col h-full">
                              <div className="relative flex items-center justify-center sm:justify-start">
                                <span 
                                  className={`inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-xs sm:text-sm font-medium ${
                                    isToday && isCurrentMonth 
                                      ? 'bg-indigo-600 text-white' 
                                      : isSelected 
                                        ? 'text-white font-semibold' 
                                        : ''
                                  }`}
                                >
                                  {day.date.getDate()}
                                </span>
                                {hasBirthday && (
                                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-pink-500"></span>
                                )}
                              </div>
                              
                              {/* Event Dots */}
                              {dayEvents.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-0.5 sm:gap-1 justify-center sm:justify-start">
                                  {dayEvents.slice(0, 3).map((event, i) => (
                                    <div 
                                      key={i}
                                      className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: colorMap[event.color] || '#6366F1' }}
                                      title={event.title}
                                    ></div>
                                  ))}
                                  {dayEvents.length > 3 && (
                                    <span className="text-[10px] sm:text-xs text-gray-400">+{dayEvents.length - 3}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Event Modal */}
        <AnimatePresence>
          {showEventModal && (
            <EventModal 
              isOpen={showEventModal} 
              onClose={() => setShowEventModal(false)}
              onSave={async (event) => {
                const success = await handleAddEvent(event);
                if (success) {
                  setShowEventModal(false);
                }
              }}
              isSaving={isSaving}
              selectedDate={date}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

